import { useMemo } from 'react'
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Legend
} from 'recharts'
import { useTheme } from '../context/ThemeContext'

function MonthlyQuarterlyView({ data, loading, viewMode = 'gross' }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const isNet = viewMode === 'net'

    const monthly = useMemo(() => {
        if (!data?.monthly) return []
        return data.monthly.filter(m =>
            isNet ? (m.netOilActual > 0 || m.netOilForecast > 0) : (m.grossOilActual > 0 || m.grossOilForecast > 0)
        )
    }, [data, isNet])

    const quarterly = useMemo(() => {
        if (!data?.quarterly) return []
        return data.quarterly.filter(q =>
            isNet ? (q.netOilActual > 0 || q.netOilForecast > 0) : (q.grossOilActual > 0 || q.grossOilForecast > 0)
        )
    }, [data, isNet])

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    if (!data || monthly.length === 0) {
        return (
            <div className={`chart-container flex items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <p className="text-lg">No monthly data available</p>
            </div>
        )
    }

    const oilActualKey = isNet ? 'netOilActual' : 'grossOilActual'
    const oilForecastKey = isNet ? 'netOilForecast' : 'grossOilForecast'
    const varianceKey = isNet ? 'netOilVariancePct' : 'oilVariancePct'
    const prefix = isNet ? 'Net' : 'Gross'

    const tooltipStyle = {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        borderRadius: '8px',
        color: isDark ? '#e2e8f0' : '#1f2937'
    }

    const gridColor = isDark ? '#334155' : '#e5e7eb'
    const axisColor = isDark ? '#94a3b8' : '#6b7280'

    // Custom label for variance % above bars — bold & highlighted
    const VarianceLabel = ({ x, y, width, index, data: chartData }) => {
        if (!chartData || !chartData[index]) return null
        const v = chartData[index][varianceKey]
        if (v === null || v === undefined) return null
        const color = v >= 0 ? '#16a34a' : '#dc2626'
        const text = `${v > 0 ? '+' : ''}${v}%`
        return (
            <g>
                <rect
                    x={x + width / 2 - 22}
                    y={y - 20}
                    width={44}
                    height={16}
                    rx={3}
                    fill={v >= 0 ? '#dcfce7' : '#fef2f2'}
                    stroke={v >= 0 ? '#86efac' : '#fca5a5'}
                    strokeWidth={0.5}
                />
                <text
                    x={x + width / 2}
                    y={y - 9}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={700}
                    fill={color}
                >
                    {text}
                </text>
            </g>
        )
    }

    // Variance label for horizontal quarterly bars
    const QuarterlyVarianceLabel = ({ x, y, width, height, index, data: chartData }) => {
        if (!chartData || !chartData[index]) return null
        const v = isNet ? chartData[index].netOilVariancePct : chartData[index].oilVariancePct
        if (v === null || v === undefined) return null
        const color = v >= 0 ? '#16a34a' : '#dc2626'
        const text = `${v > 0 ? '+' : ''}${v}%`
        return (
            <g>
                <rect
                    x={x + width + 6}
                    y={y + height / 2 - 9}
                    width={50}
                    height={18}
                    rx={3}
                    fill={v >= 0 ? '#dcfce7' : '#fef2f2'}
                    stroke={v >= 0 ? '#86efac' : '#fca5a5'}
                    strokeWidth={0.5}
                />
                <text
                    x={x + width + 31}
                    y={y + height / 2 + 4}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={700}
                    fill={color}
                >
                    {text}
                </text>
            </g>
        )
    }

    const formatNum = (v) => v?.toLocaleString() || '0'

    const MonthlyTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        const d = payload[0]?.payload
        if (!d) return null
        return (
            <div style={tooltipStyle} className="p-3 shadow-lg text-sm">
                <p className="font-semibold mb-1">{label}</p>
                <p style={{ color: '#22c55e' }}>{prefix} Oil Actual: {formatNum(d[oilActualKey])} BBL</p>
                <p style={{ color: '#9ca3af' }}>{prefix} Oil Forecast: {formatNum(d[oilForecastKey])} BBL</p>
                {d[varianceKey] !== null && (
                    <p style={{ color: d[varianceKey] >= 0 ? '#22c55e' : '#ef4444' }}>
                        Variance: {d[varianceKey] > 0 ? '+' : ''}{d[varianceKey]}%
                    </p>
                )}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Monthly Oil vs Forecast Bar Chart */}
            <div className="chart-container">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                    Monthly {prefix} Oil vs Forecast
                    <span className={`ml-3 text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Variance % shown above bars
                    </span>
                </h2>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={monthly} margin={{ top: 25, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis
                            dataKey="label"
                            tick={{ fill: axisColor, fontSize: 11 }}
                            angle={-35}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis
                            tick={{ fill: axisColor, fontSize: 11 }}
                            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                        />
                        <Tooltip content={<MonthlyTooltip />} />
                        <Legend
                            wrapperStyle={{ fontSize: 12, color: axisColor }}
                        />
                        <Bar dataKey={oilActualKey} name="Actual" fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={40}>
                            <LabelList content={<VarianceLabel data={monthly} />} />
                        </Bar>
                        <Bar dataKey={oilForecastKey} name="Forecast" fill="#9ca3af" radius={[3, 3, 0, 0]} maxBarSize={40} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Monthly Data Table */}
            <div className="chart-container">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-3`}>
                    Monthly Detail
                </h2>
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th className="text-right">{prefix} Oil Production</th>
                                <th className="text-right">{prefix} Oil Forecast</th>
                                <th className="text-right">Oil Variance %</th>
                                <th className="text-right">{prefix} Gas Production</th>
                                <th className="text-right">{prefix} Gas Forecast</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...monthly].reverse().map((m, i) => {
                                const oilActual = m[oilActualKey]
                                const oilForecast = m[oilForecastKey]
                                const variance = m[varianceKey]
                                const gasActual = isNet ? m.netGasActual : m.grossGasActual
                                const gasForecast = isNet ? m.netGasForecast : m.grossGasForecast
                                return (
                                    <tr key={i}>
                                        <td className="font-medium whitespace-nowrap">{m.label}</td>
                                        <td className="text-right font-mono">{formatNum(oilActual)}</td>
                                        <td className="text-right font-mono">{formatNum(oilForecast)}</td>
                                        <td className="text-right">
                                            {variance !== null ? (
                                                <span className={`inline-block px-2 py-0.5 rounded font-mono font-bold text-sm ${
                                                    variance >= 0
                                                        ? isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                                        : isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {variance > 0 ? '+' : ''}{variance}%
                                                </span>
                                            ) : '--'}
                                        </td>
                                        <td className="text-right font-mono">{formatNum(gasActual)}</td>
                                        <td className="text-right font-mono">{formatNum(gasForecast)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Quarterly Oil vs Forecast Bar Chart (horizontal) */}
            {quarterly.length > 0 && (
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                        Quarterly {prefix} Oil vs Forecast
                    </h2>
                    <ResponsiveContainer width="100%" height={Math.max(200, quarterly.length * 70 + 40)}>
                        <BarChart data={quarterly} layout="vertical" margin={{ top: 10, right: 80, left: 60, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis
                                type="number"
                                tick={{ fill: axisColor, fontSize: 11 }}
                                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            />
                            <YAxis
                                type="category"
                                dataKey="label"
                                tick={{ fill: axisColor, fontSize: 12 }}
                                width={70}
                            />
                            <Tooltip content={<MonthlyTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} />
                            <Bar dataKey={oilActualKey} name="Actual" fill="#22c55e" radius={[0, 3, 3, 0]} maxBarSize={24}>
                                <LabelList content={<QuarterlyVarianceLabel data={quarterly} />} />
                            </Bar>
                            <Bar dataKey={oilForecastKey} name="Forecast" fill="#9ca3af" radius={[0, 3, 3, 0]} maxBarSize={24} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Quarterly Summary Table */}
            {quarterly.length > 0 && (
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-3`}>
                        Quarterly Summary
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Quarter</th>
                                    <th className="text-right">{prefix} Oil Production</th>
                                    <th className="text-right">{prefix} Oil Forecast</th>
                                    <th className="text-right">Variance %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...quarterly].reverse().map((q, i) => {
                                    const variance = isNet ? q.netOilVariancePct : q.oilVariancePct
                                    return (
                                        <tr key={i}>
                                            <td className="font-medium">{q.label}</td>
                                            <td className="text-right font-mono">{formatNum(q[oilActualKey])}</td>
                                            <td className="text-right font-mono">{formatNum(q[oilForecastKey])}</td>
                                            <td className="text-right">
                                                {variance !== null ? (
                                                    <span className={`inline-block px-2 py-0.5 rounded font-mono font-bold text-sm ${
                                                        variance >= 0
                                                            ? isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                                            : isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                                    }`}>
                                                        {variance > 0 ? '+' : ''}{variance}%
                                                    </span>
                                                ) : '--'}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MonthlyQuarterlyView
